<template>
    <div ref="rootEl" class="cc-graph flex items-center justify-center w-full h-full">
        <!-- Scales the fixed-geometry mindmap to fit whatever container it's placed in -->
        <div class="mm-scaler" :style="{ '--fit-scale': fitScale }">
            <div class="mindmap" aria-hidden="true">
                <!-- Connector spokes from the hub out to each node; drawn behind everything -->
                <svg aria-hidden="true" class="mm-lines" :viewBox="`0 0 ${W} ${H}`" preserveAspectRatio="xMidYMid meet">
                    <line
                        v-for="node in nodes"
                        :key="`line-${node.id}`"
                        :x1="CX"
                        :y1="CY"
                        :x2="node.lineX"
                        :y2="node.lineY"
                        :class="['mm-line', { 'is-shown': node.step <= activeStep }]"
                        :style="{ '--delay': `${node.delay}s`, stroke: node.lineColor }"
                        stroke-width="1"
                        pathLength="1" />
                </svg>

                <!-- Hub -->
                <span ref="centerEl" class="mm-center">{{ t("chat.home.coachingContext.graph.center") }}</span>

                <!-- Radial nodes: each pill is centred on a point placed at angle/radius -->
                <div
                    v-for="node in nodes"
                    :key="node.id"
                    class="mm-node"
                    :class="{ 'is-shown': node.step <= activeStep }"
                    :style="{ '--a': `${node.angle}deg`, '--r': `${node.radius}px`, '--delay': `${node.delay}s` }">
                    <span class="mm-pill" :style="{ background: node.bgColor, color: node.textColor, '--glow': node.glow }">
                        <span v-if="node.cat === 'people'" class="mm-ava">{{ node.label.charAt(0) }}</span>
                        {{ node.label }}
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
/* eslint design-system/no-off-scale-spacing: "off" -- This animated mindmap uses fixed-coordinate nodes and calibrated label padding. */
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
    // Index of the highest context ring to reveal (0 = personal, 1 = people, …). -1 hides all rings.
    activeStep: number;
    // Drops the "people" ring so the remaining rings fill the circle evenly (no gap).
    connectionsEnabled?: boolean;
}>();

const { t } = useI18n();

// Authored radial system (mirrors the design prototype). Two concentric rings that clear the hub.
const W = 372;
const H = 340;
const CX = 186;
const CY = 170;
const R_OUT = 150;
const R_IN = 104;
const START = -30; // first node sits just right of top

interface Category {
    key: string;
    itemIds: string[];
    color: string;
    bgColor: string;
    textColor: string;
    lineColor: string;
    glow: string;
}

const CATEGORIES: Category[] = [
    {
        key: "concept",
        itemIds: ["goals", "patterns", "style", "challenges"],
        color: "#CD2F7B",
        bgColor: "#fce4ef",
        textColor: "#c31a78",
        lineColor: "rgba(205, 47, 123, 0.30)",
        glow: "rgba(205, 47, 123, 0.50)",
    },
    {
        key: "people",
        itemIds: ["Marcus", "Sarah", "Jordan", "Dana", "Alex", "Taylor"],
        color: "#FB945E",
        bgColor: "#ffe9d3",
        textColor: "#c15e20",
        lineColor: "rgba(225, 140, 60, 0.32)",
        glow: "rgba(225, 140, 60, 0.55)",
    },
    {
        key: "week",
        itemIds: ["meetings", "initiatives", "deadlines", "role"],
        color: "#14C8EB",
        bgColor: "#c6e9e9",
        textColor: "#1b605d",
        lineColor: "rgba(40, 150, 146, 0.30)",
        glow: "rgba(40, 150, 146, 0.50)",
    },
    {
        key: "org",
        itemIds: ["strategy", "language", "values", "culture", "priorities", "norms"],
        color: "#5852C4",
        bgColor: "#e3e1f9",
        textColor: "#47438c",
        lineColor: "rgba(126, 120, 222, 0.32)",
        glow: "rgba(126, 120, 222, 0.55)",
    },
];

interface GraphNode {
    id: string;
    label: string;
    cat: string;
    step: number;
    angle: number;
    radius: number;
    lineX: number;
    lineY: number;
    lineColor: string;
    bgColor: string;
    textColor: string;
    glow: string;
    delay: number;
}

// Only the included categories take angular slots, so they fill the circle evenly (no gaps).
const shownCategories = computed<Category[]>(() => CATEGORIES.filter((c) => c.key !== "people" || props.connectionsEnabled !== false));

// Sample people are names, so they display as-is; every other node has a translated label.
const nodeLabels = computed<Record<string, string>>(() => ({
    goals: t("chat.home.coachingContext.graph.goals"),
    patterns: t("chat.home.coachingContext.graph.patterns"),
    style: t("chat.home.coachingContext.graph.style"),
    challenges: t("chat.home.coachingContext.graph.challenges"),
    meetings: t("chat.home.coachingContext.graph.meetings"),
    initiatives: t("chat.home.coachingContext.graph.initiatives"),
    deadlines: t("chat.home.coachingContext.graph.deadlines"),
    role: t("chat.home.coachingContext.graph.role"),
    strategy: t("chat.home.coachingContext.graph.strategy"),
    language: t("chat.home.coachingContext.graph.language"),
    values: t("chat.home.coachingContext.graph.values"),
    culture: t("chat.home.coachingContext.graph.culture"),
    priorities: t("chat.home.coachingContext.graph.priorities"),
    norms: t("chat.home.coachingContext.graph.norms"),
}));

const nodes = computed<GraphNode[]>(() => {
    const shown = shownCategories.value;
    const total = shown.reduce((n, c) => n + c.itemIds.length, 0);
    const step = 360 / total;
    const out: GraphNode[] = [];
    let g = 0;
    shown.forEach((cat, catStep) => {
        cat.itemIds.forEach((itemId, i) => {
            const angle = START + g * step;
            const radius = g % 2 === 0 ? R_OUT : R_IN;
            g++;
            const rad = (angle * Math.PI) / 180;
            out.push({
                id: `${cat.key}-${itemId}`,
                label: nodeLabels.value[itemId] ?? itemId,
                cat: cat.key,
                step: catStep,
                angle,
                radius,
                lineX: CX + radius * Math.sin(rad),
                lineY: CY - radius * Math.cos(rad),
                lineColor: cat.lineColor,
                bgColor: cat.bgColor,
                textColor: cat.textColor,
                glow: cat.glow,
                delay: 0.12 + i * 0.07,
            });
        });
    });
    return out;
});

const MAX_SCALE = 1;
const rootEl = useTemplateRef<HTMLElement>("rootEl");
const fitScale = ref(MAX_SCALE);
let resizeObserver: ResizeObserver | undefined;

function updateFitScale() {
    const el = rootEl.value;
    if (!el) {
        return;
    }
    const cw = el.clientWidth;
    const ch = el.clientHeight;
    if (cw <= 0 || ch <= 0) {
        return;
    }
    fitScale.value = Math.min(cw / W, ch / H, MAX_SCALE);
}

onMounted(() => {
    updateFitScale();
    if (rootEl.value && "ResizeObserver" in globalThis) {
        resizeObserver = new ResizeObserver(updateFitScale);
        resizeObserver.observe(rootEl.value);
    }
});

onBeforeUnmount(() => {
    resizeObserver?.disconnect();
    resizeObserver = undefined;
});

// Retrigger the hub's spotlight pulse whenever a new ring is revealed.
const centerEl = ref<HTMLElement | null>(null);
watch(
    () => props.activeStep,
    (next, prev) => {
        if (next > (prev ?? -1) && centerEl.value) {
            centerEl.value.classList.remove("is-pulsing");
            void centerEl.value.offsetWidth;
            centerEl.value.classList.add("is-pulsing");
        }
    },
);
</script>

<style scoped>
@property --r {
    syntax: "<length>";
    inherits: false;
    initial-value: 0px;
}

.mm-scaler {
    width: 372px;
    height: 340px;
    flex-shrink: 0;
    transform: scale(var(--fit-scale, 0.82));
    transform-origin: center center;
}

.mindmap {
    position: relative;
    width: 372px;
    height: 340px;
    animation: floaty 5s ease-in-out 1s infinite;
}

.mm-lines {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    overflow: visible;
}

.mm-line {
    stroke-dasharray: 1;
    stroke-dashoffset: 1;
    opacity: 0;
    stroke-width: 1;
}

.mm-line.is-shown {
    opacity: 1;
    animation: drawIn 0.6s ease-out var(--delay, 0s) both;
}

.mm-center {
    position: absolute;
    left: 50%;
    top: 50%;
    translate: -50% -50%;
    width: 74px;
    height: 74px;
    border-radius: 50%;
    box-sizing: border-box;
    background: #cd2f7b;
    border: 1.66px solid #e84c8d;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 23px;
    color: #f7f5f2;
    z-index: 2;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.1);
    animation: popIn 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s both;
}

.mm-center.is-pulsing {
    animation: centerPulse 0.7s ease-out;
}

/* Each node is a point placed radially; the pill is centred on that point. */
.mm-node {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: rotate(var(--a)) translateY(calc(-1 * var(--r))) rotate(calc(-1 * var(--a)));
    opacity: 0;
}

.mm-node.is-shown {
    animation: nodeIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) var(--delay, 0s) both;
}

.mm-node.is-shown .mm-pill {
    animation: pillGlow 0.75s ease-out var(--delay, 0s) both;
}

.mm-pill {
    position: absolute;
    left: 0;
    top: 0;
    translate: -50% -50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    width: 62px;
    box-sizing: border-box;
    font-size: 10px;
    line-height: 13px;
    padding: 3px 6px;
    border-radius: 999px;
    white-space: nowrap;
    font-weight: 500;
}

.mm-ava {
    width: 15px;
    height: 15px;
    border-radius: 50%;
    background: #f0a860;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 8px;
    font-weight: 600;
    flex-shrink: 0;
}

@keyframes popIn {
    0% {
        opacity: 0;
        transform: scale(0.4);
    }
    70% {
        opacity: 1;
        transform: scale(1.08);
    }
    100% {
        opacity: 1;
        transform: scale(1);
    }
}

@keyframes drawIn {
    from {
        stroke-dashoffset: 1;
    }
    to {
        stroke-dashoffset: 0;
    }
}

@keyframes floaty {
    0%,
    100% {
        transform: translateY(0);
    }
    50% {
        transform: translateY(-5px);
    }
}

/* Spotlight entrance: fly out from the hub, overshoot larger, then settle */
@keyframes nodeIn {
    0% {
        opacity: 0;
        scale: 0.55;
        --r: 22px;
    }
    45% {
        opacity: 1;
        scale: 1.16;
    }
    100% {
        opacity: 1;
        scale: 1;
    }
}

@keyframes pillGlow {
    0% {
        box-shadow: 0 0 0 0 rgba(0, 0, 0, 0);
    }
    45% {
        box-shadow: 0 0 18px 3px var(--glow, rgba(205, 47, 123, 0.5));
    }
    100% {
        box-shadow: 0 0 0 0 rgba(0, 0, 0, 0);
    }
}

@keyframes centerPulse {
    0% {
        box-shadow:
            0 6px 18px rgba(0, 0, 0, 0.1),
            0 0 0 0 rgba(205, 47, 123, 0.5);
    }
    45% {
        box-shadow:
            0 6px 18px rgba(0, 0, 0, 0.1),
            0 0 26px 4px rgba(205, 47, 123, 0.5);
    }
    100% {
        box-shadow:
            0 6px 18px rgba(0, 0, 0, 0.1),
            0 0 0 0 rgba(205, 47, 123, 0.5);
    }
}

@media (prefers-reduced-motion: reduce) {
    /* Same selectors as the animating rules above, so this later block wins on source order. */
    .mindmap,
    .mm-center,
    .mm-center.is-pulsing,
    .mm-node.is-shown,
    .mm-node.is-shown .mm-pill,
    .mm-line.is-shown {
        animation: none;
    }
    .mm-node.is-shown {
        opacity: 1;
    }
    .mm-line.is-shown {
        opacity: 1;
        stroke-dashoffset: 0;
    }
}
</style>
